import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  entityParticipationsService,
  type EntityParticipationFilters,
  type EntityParticipationCreateInput,
  type EntityParticipationUpdateInput,
} from '../api';
import type { EntityCompanyParticipation } from '@lognai/plugin-sdk';
import { PartnerAccessLevel } from '../types/domain-enums';
import { queryKeys } from './query-keys';

export function useEntityParticipations(
  filters?: EntityParticipationFilters,
  options?: Omit<UseQueryOptions<EntityCompanyParticipation[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.entityParticipations.list(filters || {}),
    queryFn: () => entityParticipationsService.list(filters),
    ...options,
  });
}

export function useEntityParticipation(
  participationId: string | undefined,
  options?: Omit<UseQueryOptions<EntityCompanyParticipation>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.entityParticipations.detail(participationId || ''),
    queryFn: () => {
      if (!participationId) throw new Error('Participation ID is required');
      return entityParticipationsService.getById(participationId);
    },
    enabled: !!participationId,
    ...options,
  });
}

export function useEntityParticipationsByEntity(
  entityType: string | undefined,
  entityId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.entityParticipations.byEntity(entityType || '', entityId || ''),
    queryFn: () => {
      if (!entityType || !entityId) throw new Error('Entity type and ID are required');
      return entityParticipationsService.getByEntity(entityType, entityId);
    },
    enabled: !!entityType && !!entityId,
  });
}

export function useEntityParticipationsByCompany(
  companyId: string | undefined,
  entityType?: string
) {
  return useQuery({
    queryKey: queryKeys.entityParticipations.byCompany(companyId || '', entityType),
    queryFn: () => {
      if (!companyId) throw new Error('Company ID is required');
      return entityParticipationsService.getByCompany(companyId, entityType);
    },
    enabled: !!companyId,
  });
}

export function useEntityParticipationStats(companyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.entityParticipations.stats(companyId || ''),
    queryFn: () => {
      if (!companyId) throw new Error('Company ID is required');
      return entityParticipationsService.getStats(companyId);
    },
    enabled: !!companyId,
  });
}

export function useCreateEntityParticipation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EntityParticipationCreateInput) => entityParticipationsService.create(data),
    onSuccess: () => {
      // Invalidate the whole tree — `lists()` does not match the `byEntity`/`byCompany`
      // keys used by the entity panels, so they would otherwise show stale data.
      queryClient.invalidateQueries({ queryKey: queryKeys.entityParticipations.all });
    },
  });
}

export function useUpdateEntityParticipation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      participationId,
      data,
    }: {
      participationId: string;
      data: EntityParticipationUpdateInput;
    }) => entityParticipationsService.update(participationId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.entityParticipations.detail(variables.participationId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.entityParticipations.all });
    },
  });
}

export function useDeleteEntityParticipation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (participationId: string) => entityParticipationsService.delete(participationId),
    onSuccess: () => {
      // Invalidate the whole tree so `byEntity`/`byCompany` panels refresh too.
      queryClient.invalidateQueries({ queryKey: queryKeys.entityParticipations.all });
    },
  });
}

export function useUpdateEntityParticipationAccessLevel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      participationId,
      accessLevel,
    }: {
      participationId: string;
      accessLevel: PartnerAccessLevel;
    }) => entityParticipationsService.updateAccessLevel(participationId, accessLevel),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.entityParticipations.detail(variables.participationId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.entityParticipations.all });
    },
  });
}

export function useUpdateEntityParticipationPermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      participationId,
      permissions,
    }: {
      participationId: string;
      permissions: string[];
    }) => entityParticipationsService.updatePermissions(participationId, permissions),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.entityParticipations.detail(variables.participationId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.entityParticipations.all });
    },
  });
}
